import { addToolBreadcrumb } from '@tunasong/models'
import {
  computeModulationSessionStats,
  generateModulationQuizQuestion,
  validateModulationQuizAnswer,
} from '@tunasong/music-lib'
import type {
  ModulationQuizQuestion,
  ModulationQuizResult,
  ModulationQuizVerdict,
  ModulationTechnique,
  SourceKey,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { ModulationExplorePanel } from './modulation-explore-panel.js'
import { ModulationLabSummary } from './modulation-lab-summary.js'
import { ModulationLabWelcome } from './modulation-lab-welcome.js'
import {
  parseDestinationKey,
  parseModulationSubMode,
  parseSourceKey,
  parseTechnique,
  type ModulationSubMode,
} from './modulation-lab-params.js'
import { ModulationQuizFeedback } from './modulation-quiz-feedback.js'
import { ModulationQuizQuestion as QuizQuestionPanel } from './modulation-quiz-question.js'
import styles from './modulation-lab.module.css'

const QUESTIONS_PER_SESSION = 10

const RELATED_TOOLS: RelatedTool[] = [
  { targetRoute: '/app/circle', lexiconId: 'crosslink-modulation-to-cof' },
  { targetRoute: '/app/reharm', lexiconId: 'crosslink-modulation-to-reharm' },
  { targetRoute: '/music-theory', lexiconId: 'crosslink-modulation-to-suite' },
]

type SessionState =
  | { phase: 'welcome'; preSubMode: ModulationSubMode | null }
  | {
      phase: 'exploring'
      source: SourceKey
      technique: ModulationTechnique
      destination: string
    }
  | {
      phase: 'asking-quiz'
      index: number
      question: ModulationQuizQuestion
      results: ModulationQuizResult[]
    }
  | {
      phase: 'feedback-quiz'
      index: number
      question: ModulationQuizQuestion
      verdict: ModulationQuizVerdict
      results: ModulationQuizResult[]
    }
  | { phase: 'done-quiz'; results: ModulationQuizResult[] }

/**
 * Modulation Lab v1 host. State machine: welcome → exploring | asking-quiz →
 * feedback-quiz → done-quiz. URL params `?mode=`, `?from=`, `?to=`,
 * `?technique=` pre-select at initial mount.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §7
 */
export const ModulationLab: FC = () => {
  const [searchParams] = useSearchParams()

  const [state, setState] = useState<SessionState>(() => {
    const preSubMode = parseModulationSubMode(searchParams.get('mode'))
    const preSource = parseSourceKey(searchParams.get('from'))
    const preTechnique = parseTechnique(searchParams.get('technique'))
    if (preSubMode === 'quiz') {
      return {
        phase: 'asking-quiz',
        index: 0,
        question: generateModulationQuizQuestion(),
        results: [],
      }
    }
    if (preSubMode === 'explore') {
      const source = preSource ?? 'C'
      const technique = preTechnique ?? 'pivot'
      const preDestination = parseDestinationKey(searchParams.get('to'), source, technique)
      const destination = preDestination ?? (technique === 'parallel' ? `${source}m` : 'G')
      return { phase: 'exploring', source, technique, destination }
    }
    return { phase: 'welcome', preSubMode }
  })

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'modulation-lab' })
  }, [])

  const startExplore = (): void => {
    addToolBreadcrumb('opened', { tool: 'modulation-lab', mode: 'explore' })
    setState({ phase: 'exploring', source: 'C', technique: 'pivot', destination: 'G' })
  }

  const startQuiz = (): void => {
    addToolBreadcrumb('opened', { tool: 'modulation-lab', mode: 'quiz' })
    setState({
      phase: 'asking-quiz',
      index: 0,
      question: generateModulationQuizQuestion(),
      results: [],
    })
  }

  const handleAnswer = (picked: ModulationTechnique): void => {
    if (state.phase !== 'asking-quiz') {return}
    const verdict = validateModulationQuizAnswer(state.question, picked)
    addToolBreadcrumb('answered', {
      tool: 'modulation-lab',
      subMode: 'quiz',
      correct: verdict.correct,
      technique: state.question.acceptedAnswer,
    })
    setState({
      phase: 'feedback-quiz',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const handleNext = (): void => {
    if (state.phase !== 'feedback-quiz') {return}
    const updated: ModulationQuizResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quiz', results: updated })
      return
    }
    setState({
      phase: 'asking-quiz',
      index: state.index + 1,
      question: generateModulationQuizQuestion(),
      results: updated,
    })
  }

  const exitToWelcome = (): void => setState({ phase: 'welcome', preSubMode: null })

  return (
    <div className={styles.container}>
      {state.phase === 'welcome' && (
        <ModulationLabWelcome
          preSubMode={state.preSubMode}
          onStartExplore={startExplore}
          onStartQuiz={startQuiz}
        />
      )}
      {state.phase === 'exploring' && (
        <ModulationExplorePanel
          initialSource={state.source}
          initialTechnique={state.technique}
          initialDestination={state.destination}
        />
      )}
      {state.phase === 'asking-quiz' && (
        <QuizQuestionPanel
          question={state.question}
          onAnswer={handleAnswer}
          questionNumber={state.index + 1}
          totalQuestions={QUESTIONS_PER_SESSION}
        />
      )}
      {state.phase === 'feedback-quiz' && (
        <ModulationQuizFeedback
          question={state.question}
          verdict={state.verdict}
          onNext={handleNext}
        />
      )}
      {state.phase === 'done-quiz' && (
        <ModulationLabSummary
          stats={computeModulationSessionStats(state.results)}
          onRestart={startQuiz}
          onExit={exitToWelcome}
        />
      )}
      <RelatedTools tools={RELATED_TOOLS} />
    </div>
  )
}
